import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { FileRejection, useDropzone } from 'react-dropzone';
import t from 'react-translate';
import NvFileUpload, { NvFileUploadType } from 'shared/components/nv-file-upload';
import { useAppDispatch } from 'redux/store';
import { addAdditionalResourceFiles } from 'redux/actions/help-options';
import { getAdditionalResources } from 'redux/selectors/help-options';
import { MAX_FILE_SIZE, MAX_NUMBER_OF_FILES, ACCEPTED_MIME_TYPES, ACCEPTED_FILE_EXTENSIONS } from '../constants';
import { validateFiles } from '../utils/validation';
import { extractFileRejectionErrors } from '../utils/error-messages';

interface UploadAreaProps {
  disabled: boolean;
  onValidationErrors: (errors: string[]) => void;
}

const UploadArea = ({ disabled, onValidationErrors }: UploadAreaProps) => {
  const dispatch = useAppDispatch();
  const resources = useSelector(getAdditionalResources);

  const handleFilesSelected = useCallback(
    (files: File[]) => {
      if (files.length === 0) return;

      const validation = validateFiles(files, resources.length);

      if (!validation.isValid) {
        onValidationErrors(validation.errors);
        return;
      }

      dispatch(addAdditionalResourceFiles(files));
    },
    [dispatch, resources.length, onValidationErrors],
  );

  const handleFileErrors = useCallback(
    (fileRejections: FileRejection[]) => {
      const errors = extractFileRejectionErrors(fileRejections);

      if (errors.length > 0) {
        onValidationErrors(errors);
      }
    },
    [onValidationErrors],
  );

  const { getRootProps, isDragActive } = useDropzone({
    onDrop: handleFilesSelected,
    onDropRejected: handleFileErrors,
    accept: ACCEPTED_FILE_EXTENSIONS,
    noClick: true,
    disabled,
    maxFiles: MAX_NUMBER_OF_FILES,
    maxSize: MAX_FILE_SIZE,
  });

  return (
    <div
      className={`${disabled ? 'disabled-upload' : ''} ${isDragActive ? 'drag-active' : ''}`}
      {...getRootProps({ tabIndex: -1 })}
    >
      <NvFileUpload
        customText={t.INSTITUTIONS.ADVANCED_SETTINGS.HELP_OPTIONS.AI_ASSISTANT.ADDITIONAL_RESOURCES.DRAG_DROP_AREA()}
        type={NvFileUploadType.DOCUMENT}
        maxSize='50MB'
        maxFiles={MAX_NUMBER_OF_FILES}
        disabled={disabled}
        multiple
        showInfoText
        showactionButton={false}
        onUploadFile={handleFilesSelected}
        onSelectError={handleFileErrors}
        dataQa='additional-resources-upload'
        customAcceptedPatterns={ACCEPTED_MIME_TYPES}
      />
    </div>
  );
};

export default UploadArea;

